import type { ChatMessage, Conversation, Preset } from '../../core/types';

export interface ContactThread {
  key: string;
  presetId: string | null;
  title: string;
  avatar?: string;
  conversations: Conversation[];
  latestConversation: Conversation;
  lastMessage: ChatMessage | null;
  unreadCount: number;
  lastActivityAt: number;
}

function latestAt(conversation: Conversation, messages: readonly ChatMessage[]): number {
  return messages.reduce((latest, message) => Math.max(latest, message.createdAt), conversation.updatedAt);
}

function compareMessages(a: ChatMessage, b: ChatMessage): number {
  return a.createdAt - b.createdAt || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
}

export function unreadLabel(count: number): string {
  if (!Number.isFinite(count) || count <= 0) return '';
  return count > 99 ? '99+' : String(Math.floor(count));
}

/**
 * The inbox shows one row per contact. Legacy conversations without a preset
 * remain visible as standalone rows so upgrading never hides local history.
 */
export function buildContactThreads(
  conversations: readonly Conversation[],
  presets: readonly Preset[],
  messages: readonly ChatMessage[],
): ContactThread[] {
  const presetById = new Map(presets.map((preset) => [preset.id, preset]));
  const messagesByConversation = new Map<string, ChatMessage[]>();
  for (const message of messages) {
    const rows = messagesByConversation.get(message.conversationId);
    if (rows === undefined) messagesByConversation.set(message.conversationId, [message]);
    else rows.push(message);
  }
  for (const rows of messagesByConversation.values()) rows.sort(compareMessages);

  const grouped = new Map<string, { presetId: string | null; conversations: Conversation[] }>();
  for (const conversation of conversations) {
    const key = conversation.presetId ? `preset:${conversation.presetId}` : `conversation:${conversation.id}`;
    const group = grouped.get(key);
    if (group === undefined) grouped.set(key, { presetId: conversation.presetId ?? null, conversations: [conversation] });
    else group.conversations.push(conversation);
  }

  const threads: ContactThread[] = [];
  for (const [key, group] of grouped) {
    const preset = group.presetId === null ? null : presetById.get(group.presetId) ?? null;
    const sortedConversations = [...group.conversations].sort((a, b) => {
      const aMessages = messagesByConversation.get(a.id) ?? [];
      const bMessages = messagesByConversation.get(b.id) ?? [];
      return latestAt(b, bMessages) - latestAt(a, aMessages) || (a.id < b.id ? 1 : -1);
    });
    const latestConversation = sortedConversations[0];
    if (latestConversation === undefined) continue;
    let lastMessage: ChatMessage | null = null;
    let unreadCount = 0;
    for (const conversation of sortedConversations) {
      for (const message of messagesByConversation.get(conversation.id) ?? []) {
        if (lastMessage === null || compareMessages(message, lastMessage) > 0) lastMessage = message;
        // Legacy assistant messages have no readAt and are treated as read.
        // New background replies are stored with readAt=0 until opened.
        if (message.role === 'assistant' && message.readAt === 0) unreadCount += 1;
      }
    }
    threads.push({
      key,
      presetId: group.presetId,
      title: preset?.name ?? latestConversation.title,
      ...(preset?.avatar ? { avatar: preset.avatar } : {}),
      conversations: sortedConversations,
      latestConversation,
      lastMessage,
      unreadCount,
      lastActivityAt: lastMessage?.createdAt ?? latestConversation.updatedAt,
    });
  }
  return threads.sort((a, b) => b.lastActivityAt - a.lastActivityAt || (a.key < b.key ? 1 : -1));
}
